import React, { useState } from 'react';
import { generateDummyPatients, generateDummySession } from '../utils/dummyChiropracticData';
import SOAPNoteGenerator from '../components/SOAPNoteGenerator';
import SessionNotes from '../components/SessionNotes';
import ChiropracticPatientComparison from '../components/ChiropracticPatientComparison';
import { CollapsiblePanel } from '../components/CollapsiblePanel';
import { Activity, Users, FileText, BarChart3, Play, Pause, Square } from 'lucide-react';

export default function ChiropracticSessionView() {
  const [selectedPatient] = useState(generateDummyPatients()[0]);
  const [currentSession, setCurrentSession] = useState(generateDummySession(selectedPatient.id));
  const [sessionNotes, setSessionNotes] = useState(currentSession.notes);
  const [isSessionActive, setIsSessionActive] = useState(true);
  const [activeTab, setActiveTab] = useState<'session' | 'comparison'>('session');
  
  // Update session when notes change
  const handleNotesChange = (notes: string) => {
    setSessionNotes(notes);
    setCurrentSession(prev => ({
      ...prev,
      notes: notes
    }));
  };

  const handleStartSession = () => {
    setIsSessionActive(true);
    const newSession = generateDummySession(selectedPatient.id);
    setCurrentSession(newSession);
    setSessionNotes(newSession.notes);
  };

  const handlePauseSession = () => {
    setIsSessionActive(false);
  };

  const handleEndSession = () => {
    setIsSessionActive(false);
    // In a real app, this would save the session to the database
    console.log('Session ended and saved:', currentSession);
  };

  const handleSelectPatient = () => {
    const patients = generateDummyPatients();
    const randomPatient = patients[Math.floor(Math.random() * patients.length)];
    const newSession = generateDummySession(randomPatient.id);
    setCurrentSession(newSession);
    setSessionNotes(newSession.notes);
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Header */}
      <div className="bg-gray-800 border-b border-gray-700 p-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">Chiropractic Session Management</h1>
            <p className="text-gray-400 mt-1">AI-powered SOAP notes and session tracking</p>
          </div>
          
          {/* Tab Navigation */}
          <div className="flex space-x-4">
            <button
              onClick={() => setActiveTab('session')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === 'session' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              <FileText className="w-4 h-4 inline mr-2" />
              Session View
            </button>
            <button
              onClick={() => setActiveTab('comparison')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === 'comparison' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              <BarChart3 className="w-4 h-4 inline mr-2" />
              Analytics
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      {activeTab === 'session' ? (
        <div className="p-6 space-y-6">
          {/* Patient Info and Session Controls */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Patient Information Panel */}
            <CollapsiblePanel
              title="Patient Information"
              icon={<Users className="w-6 h-6 text-blue-400" />}
              defaultExpanded={true}
            >
              <div className="space-y-4">
                <div className="bg-gray-700 rounded-lg p-4">
                  <h3 className="text-lg font-semibold mb-3">{selectedPatient.first_name} {selectedPatient.last_name}</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-400">Diagnosis:</span>
                      <p className="font-medium">{selectedPatient.diagnosis}</p>
                    </div>
                    <div>
                      <span className="text-gray-400">Sessions:</span>
                      <p className="font-medium">{selectedPatient.sessions_count}</p>
                    </div>
                    <div>
                      <span className="text-gray-400">Last Visit:</span>
                      <p className="font-medium">{selectedPatient.last_visit}</p>
                    </div>
                    <div>
                      <span className="text-gray-400">Status:</span>
                      <p className={`font-medium ${isSessionActive ? 'text-green-400' : 'text-gray-400'}`}>
                        {isSessionActive ? 'Active Session' : 'No Active Session'}
                      </p>
                    </div>
                  </div>
                </div>
                
                <button
                  onClick={handleSelectPatient}
                  className="w-full py-2 bg-blue-600 hover:bg-blue-700 rounded-md text-sm font-medium transition-colors"
                >
                  Select Different Patient
                </button>
              </div>
            </CollapsiblePanel>

            {/* Session Controls Panel */}
            <CollapsiblePanel
              title="Session Controls"
              icon={<Activity className="w-6 h-6 text-green-400" />}
              defaultExpanded={true}
            >
              <div className="space-y-4">
                <div className="bg-gray-700 rounded-lg p-4">
                  <h3 className="text-lg font-semibold mb-3">Current Session</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-400">Session ID:</span>
                      <p className="font-medium font-mono text-xs">{currentSession.id.substring(0, 8)}...</p>
                    </div>
                    <div>
                      <span className="text-gray-400">Duration:</span>
                      <p className="font-medium">{Math.floor(currentSession.duration / 60)} minutes</p>
                    </div>
                    <div>
                      <span className="text-gray-400">Start Time:</span>
                      <p className="font-medium">{new Date(currentSession.startTime).toLocaleTimeString()}</p>
                    </div>
                    <div>
                      <span className="text-gray-400">Metrics Captured:</span>
                      <p className="font-medium">{currentSession.metrics.length} samples</p>
                    </div>
                  </div>
                </div>
                
                <div className="flex space-x-3">
                  <button
                    onClick={handleStartSession}
                    disabled={isSessionActive}
                    className={`flex-1 flex items-center justify-center py-2 rounded-md text-sm font-medium transition-colors ${
                      isSessionActive 
                        ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                        : 'bg-green-600 hover:bg-green-700 text-white'
                    }`}
                  >
                    <Play className="w-4 h-4 mr-2" />
                    Start Session
                  </button>
                  
                  <button
                    onClick={handlePauseSession}
                    disabled={!isSessionActive}
                    className={`flex-1 flex items-center justify-center py-2 rounded-md text-sm font-medium transition-colors ${
                      !isSessionActive 
                        ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                        : 'bg-yellow-600 hover:bg-yellow-700 text-white'
                    }`}
                  >
                    <Pause className="w-4 h-4 mr-2" />
                    Pause
                  </button>
                  
                  <button
                    onClick={handleEndSession}
                    disabled={!isSessionActive}
                    className={`flex-1 flex items-center justify-center py-2 rounded-md text-sm font-medium transition-colors ${
                      !isSessionActive 
                        ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                        : 'bg-red-600 hover:bg-red-700 text-white'
                    }`}
                  >
                    <Square className="w-4 h-4 mr-2" />
                    End Session
                  </button>
                </div>
              </div>
            </CollapsiblePanel>
          </div>

          {/* Main Session Content */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Session Notes */}
            <SessionNotes
              value={sessionNotes}
              onChange={handleNotesChange}
              isSessionActive={isSessionActive}
            />
            
            {/* SOAP Note Generator */}
            <SOAPNoteGenerator
              session={{...currentSession, notes: sessionNotes}}
              patient={selectedPatient}
            />
          </div>

          {/* Device Metrics Display */}
          <CollapsiblePanel
            title="Real-time Metrics"
            subtitle={`${currentSession.metrics.length} measurement samples`}
            icon={<Activity className="w-6 h-6 text-purple-400" />}
            defaultExpanded={false}
          >
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {currentSession.selectedMetrics.map((metricKey) => {
                const latestMetric = currentSession.metrics[currentSession.metrics.length - 1];
                const metricValue = latestMetric?.[metricKey as keyof typeof latestMetric];
                
                const getMetricDisplay = (key: string, value: any) => {
                  switch (key) {
                    case 'muscleTensionScore':
                      return { name: 'Muscle Tension', value: `${value?.toFixed(1) || 'N/A'}/100`, color: value > 70 ? 'text-red-400' : value > 50 ? 'text-yellow-400' : 'text-green-400' };
                    case 'treatmentEffectiveness':
                      return { name: 'Treatment Effectiveness', value: `${value?.toFixed(1) || 'N/A'}%`, color: value > 80 ? 'text-green-400' : value > 60 ? 'text-yellow-400' : 'text-red-400' };
                    case 'painResponse':
                      return { name: 'Pain Response', value: `${value?.toFixed(1) || 'N/A'}/10`, color: value > 7 ? 'text-red-400' : value > 4 ? 'text-yellow-400' : 'text-green-400' };
                    case 'rangeOfMotion':
                      return { name: 'Range of Motion', value: `${value?.toFixed(0) || 'N/A'}°`, color: value > 80 ? 'text-green-400' : value > 60 ? 'text-yellow-400' : 'text-red-400' };
                    case 'tissueElasticity':
                      return { name: 'Tissue Elasticity', value: `${value?.toFixed(1) || 'N/A'}mm`, color: value > 3.5 ? 'text-green-400' : value > 2.5 ? 'text-yellow-400' : 'text-red-400' };
                    case 'triggerPointIntensity':
                      return { name: 'Trigger Point Intensity', value: `${value?.toFixed(1) || 'N/A'}/10`, color: value > 7 ? 'text-red-400' : value > 4 ? 'text-yellow-400' : 'text-green-400' };
                    default:
                      return { name: key, value: `${value?.toFixed(1) || 'N/A'}`, color: 'text-gray-400' };
                  }
                };

                const display = getMetricDisplay(metricKey, metricValue);

                return (
                  <div key={metricKey} className="bg-gray-700 rounded-lg p-4 text-center">
                    <h4 className="text-xs text-gray-400 mb-1">{display.name}</h4>
                    <p className={`text-lg font-bold ${display.color}`}>{display.value}</p>
                  </div>
                );
              })}
            </div>
          </CollapsiblePanel>
        </div>
      ) : (
        <ChiropracticPatientComparison />
      )}
    </div>
  );
}